/**
 * M27 Step 5 — Import Employees (PR 5).
 */

import { useQuery } from "@tanstack/react-query";
import {
  ChevronRight,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Plus,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { AIHelpsHereCallout } from "../../components/onboarding/AIHelpsHereCallout";
import { ComingSoonBadge } from "../../components/ui";
import { api } from "../../lib/api";
import { useLegalEntityGate } from "../../lib/legalEntity";
import { StepLayout } from "./StepLayout";

export function Step5EmployeesReal() {
  const { t } = useTranslation();
  const { legalEntityId: le, gate } = useLegalEntityGate();
  const headlines = useQuery({
    queryKey: ["mc-headlines", le ?? "_all_"],
    queryFn: () => api.missionControlHeadlines(le || undefined),
    enabled: !!le,
  });

  if (gate) return gate;
  const count =
    headlines.data?.cards?.find((c) => c.key === "headcount")?.count ?? 0;

  return (
    <StepLayout
      slug="employees"
      title={t("app.onb_step_employees")}
      subtitle={t("app.s5_subtitle")}
      continueLabel={t("app.s5_continue_to_approvals")}
    >
      <div className="flex flex-col gap-5">
        <AIHelpsHereCallout
          message={t("app.s5_ai_msg")}
          detail={t("app.s5_ai_detail")}
        />

        <section className="rounded-xl border border-border-subtle bg-surface-default p-5">
          <header className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-ai-bg text-ai">
              <Users size={16} aria-hidden />
            </span>
            <div>
              <h2 className="text-sm font-semibold text-text-primary">
                {t("app.s5_team_so_far")}
              </h2>
              <p className="mt-0.5 text-xs text-text-secondary">
                {t("app.s5_team_body")}
              </p>
            </div>
            <span className="ms-auto rounded-full bg-surface-sunken px-3 py-1 text-sm font-semibold tabular-nums text-text-primary">
              {count}
            </span>
          </header>
        </section>

        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-text-primary">
            {t("app.s5_pick_source")}
          </h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <SourceCard
              icon={FileSpreadsheet}
              title={t("app.s5_csv_title")}
              body={t("app.s5_csv_body")}
              cta={t("app.s5_open_import")}
              to="/operations/import"
              ai={t("app.s5_csv_ai")}
            />
            <SourceCard
              icon={FileText}
              title={t("app.s5_qiwa_title")}
              body={t("app.s5_qiwa_body")}
              cta={t("app.s5_open_import")}
              to="/operations/import"
              ai={t("app.s5_qiwa_ai")}
              comingSoon
              expectedRelease={t(
                "app.s5_folder_import_release",
                "Import v2",
              )}
            />
            <SourceCard
              icon={FolderOpen}
              title={t("app.s5_mixed_title")}
              body={t("app.s5_mixed_body")}
              cta={t("app.s5_open_import")}
              to="/operations/import"
              ai={t("app.s5_mixed_ai")}
              comingSoon
              expectedRelease={t(
                "app.s5_folder_import_release",
                "Import v2",
              )}
            />
            <SourceCard
              icon={Plus}
              title={t("app.s5_manual_title")}
              body={t("app.s5_manual_body")}
              cta={t("app.s5_open_wizard")}
              to="/people/employees/new"
            />
          </div>
        </section>

        <p className="text-xs text-text-tertiary">
          {t("app.s5_footer_note")}
        </p>
      </div>
    </StepLayout>
  );
}

function SourceCard({
  icon: Icon,
  title,
  body,
  ai,
  cta,
  to,
  comingSoon = false,
  expectedRelease = "Import v2",
}: {
  icon: typeof FileSpreadsheet;
  title: string;
  body: string;
  ai?: string;
  cta: string;
  to: string;
  comingSoon?: boolean;
  expectedRelease?: string;
}) {
  const { t } = useTranslation();
  const comingSoonLabel = t("app.coming_soon", "Coming soon");

  const cardBody = (
    <>
      <header className="flex items-center gap-2">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-sunken text-text-secondary">
          <Icon size={14} aria-hidden />
        </span>
        <h3 className="min-w-0 flex-1 text-sm font-semibold text-text-primary">
          {title}
        </h3>
      </header>
      <p className="mt-2 text-xs text-text-secondary">{body}</p>
      {ai && (
        <p className="mt-2 inline-flex items-start gap-1 rounded bg-ai-bg px-2 py-1 text-xs text-ai-deep">
          <span aria-hidden>✦</span>
          <span>{ai}</span>
        </p>
      )}
    </>
  );

  return (
    <article
      className="relative flex flex-col rounded-xl border border-border-subtle bg-surface-default p-4"
      aria-label={
        comingSoon ? `${title} — ${comingSoonLabel} — ${expectedRelease}` : undefined
      }
    >
      {comingSoon && (
        <ComingSoonBadge
          expectedRelease={expectedRelease}
          feature="onboarding-folder-import"
          className="absolute top-4 end-4 z-10"
        >
          {comingSoonLabel}
        </ComingSoonBadge>
      )}
      {comingSoon ? (
        <div className="pointer-events-none select-none pe-28 blur-[2px] opacity-60">
          {cardBody}
        </div>
      ) : (
        cardBody
      )}
      {comingSoon ? (
        <ComingSoonBadge
          expectedRelease={expectedRelease}
          feature="onboarding-folder-import-cta"
          className="mt-3 self-center"
        >
          {cta}
        </ComingSoonBadge>
      ) : (
        <Link
          to={to}
          className="mt-3 inline-flex items-center justify-center gap-1 rounded-md border border-border-subtle bg-surface-default px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
        >
          {cta}
          <ChevronRight size={14} aria-hidden className="rtl:rotate-180" />
        </Link>
      )}
    </article>
  );
}
